//#region IMPORT
import Commons from '../../../Services/Common/Common';
import Global from '../../../Services/Common/Global';
import { showSnackbar } from '../../../Reducer/SnackbarReducer';
import { apiRequest } from '../../../Services/Common/ApiRequest';
import dayjs from 'dayjs';
//#endregion

/**
 * 안전보건교육 - 교육일지 (SAT_BASIC_02) 전용 로직
 * other 케이스에서 사용
 */

// HTML <br> 태그를 줄바꿈으로 변환하는 함수
const convertHtmlBrToNewLine = (value) => {
  if (!value) return '';

  let text = value.toString();

  text = text.replace(/<\/p>\s*<br\s*\/?>\s*<p[^>]*>/gi, '\n');

  text = text.replace(/<\/p>\s*<p[^>]*>/gi, '\n');

  text = text.replace(/<br\s*\/?>/gi, '\n');

  text = text.replace(/<\/?p[^>]*>/gi, '');

  text = text.replace(/<\/?[a-z][^>]*>/gi, '');

  text = text.replace(/&nbsp;/gi, ' ');

  text = text.replace(/\r\n|\r/g, '\n');

  text = text.replace(/\n{2,}/g, '\n');
  text = text.replace(/^\n+/, '');

  return text.trim();
};

// URL 수정 함수
const fixUrl = (url) => {
  if (!url || typeof url !== 'string') return '';
  const parts = url.split('/');
  const fileName = parts.pop();
  const encodedFileName = encodeURIComponent(fileName);
  return [...parts, encodedFileName].join('/');
};

// 이미지를 Base64로 변환하는 함수
const ConvertImageToBase64 = async (url) => {
  try {
    const response = await fetch(url);
    if (!response.ok) return '';
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader.result);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('ConvertImageToBase64 error:', error);
    return null;
  }
};

// EDU_TYPE 코드를 트리 구조로 변환하는 함수
const convertToEduCodes = (data) => {
  const result = {
    EDU_TYPE: [], // 교육의 구분
    EDU_NM: {}, // 교육명
    EDU_CONTENTS: {}, // 교육 내용
  };

  data.forEach((item) => {
    const group = item.groupCd;
    const code = item.code;
    const name = item.codeNm;

    if (group === 'EDU_GUBUN') {
      result.EDU_TYPE.push({ code, name });
      return;
    }

    if (/^EG_\d{3,4}$/.test(group)) {
      const typeCode = group.replace('EG_', '');

      if (!result.EDU_NM[typeCode]) {
        result.EDU_NM[typeCode] = [];
      }

      result.EDU_NM[typeCode].push({
        code,
        name,
      });

      return;
    }

    if (/^EG_\d{3,4}_\d{3,4}$/.test(group)) {
      const eduNameCode = group;

      if (!result.EDU_CONTENTS[eduNameCode]) {
        result.EDU_CONTENTS[eduNameCode] = [];
      }

      result.EDU_CONTENTS[eduNameCode].push({
        code,
        name,
      });

      return;
    }
  });

  return result;
};

// 코드 데이터 조회 함수 (EDU_TYPE 등)
const SearchCode = async (dispatch, setLoading) => {
  setLoading(true);
  try {
    const result = await apiRequest(
      'post',
      Global.urls.Utility.getCodeInfo,
    );

    const data = result.data;
    if (!Array.isArray(data) || data.length === 0) {
      dispatch(showSnackbar({ message: '코드가 없습니다.', type: 'info' }));
      return { EDU_TYPE: [] };
    }

    if (data[0]?.type === 'Error') {
      dispatch(showSnackbar({ message: data[0].message, type: 'error' }));
      return { EDU_TYPE: [] };
    }

    const tree = convertToEduCodes(data);
    return tree;
  } catch (e) {
    dispatch(showSnackbar({ message: e.message, type: 'error' }));
    return { EDU_TYPE: [] };
  } finally {
    setLoading(false);
  }
};

// 안전보건교육 - 교육일지 조회 함수
export const SearchLogic1 = async (apiUrl, procedureParams, dispatch, setLoading) => {
  setLoading(true);

  try {
    const result = await apiRequest(
      'post',
      apiUrl,
      procedureParams
    );

    let data = result.data;
    if (data.length === 0) {
      dispatch(
        showSnackbar({
          message: '데이터가 없습니다.',
          type: 'info',
        })
      );
      return [];
    } else if (data[0]?.type === 'Error') {
      dispatch(showSnackbar({ message: data[0].message, type: 'error' }));
      return [];
    } else {
      const sanitized = data.map((row) => ({
        ...row,
        eduTypeNm: convertHtmlBrToNewLine(row.eduTypeNm),
        eduNmNm: convertHtmlBrToNewLine(row.eduNmNm),
        eduContentsNm: convertHtmlBrToNewLine(row.eduContentsNm),
      }));

      return Commons.InsertNumber(sanitized);
    }
  } catch (e) {
    console.error(e);
    dispatch(showSnackbar({ message: e.message, type: 'error' }));
    return [];
  } finally {
    setLoading(false);
  }
};

// 안전보건교육 - 교육일지 인쇄 함수 (관리자 TRA_CUSTOM_02F PrintLogic1과 동일 로직)
export const PrintLogic1 = async (
  documentData,
  dispatch,
  setLoading,
  rowData1,
  searchRef
) => {
  setLoading(true);
  try {
    let baseRow = null;
    if (documentData) {
      baseRow = Array.isArray(documentData) ? documentData[0] : documentData;
    } else if (rowData1 && Array.isArray(rowData1) && rowData1.length > 0) {
      baseRow = rowData1[0];
    } else if (rowData1 && !Array.isArray(rowData1)) {
      baseRow = rowData1;
    }

    const baseTpmId =
      baseRow?.tpmId ?? baseRow?.TPM_ID ?? '';
    if (!baseRow || !baseTpmId) {
      dispatch(
        showSnackbar({
          message: '인쇄할 교육일지를 선택해주세요.',
          type: 'warning',
        })
      );
      return null;
    }

    let eduImg1 = null;
    let eduImg2 = null;
    const eduImagePath1 =
      baseRow.eduImageFilePath1 ||
      baseRow.EduImageFilePath1 ||
      baseRow.EDU_IMAGE_FILE_PATH1 ||
      '';
    const eduImagePath2 =
      baseRow.eduImageFilePath2 ||
      baseRow.EduImageFilePath2 ||
      baseRow.EDU_IMAGE_FILE_PATH2 ||
      '';
    if (eduImagePath1 || eduImagePath2) {
      try {
        const loadBase64 = async (rawPath) => {
          if (!rawPath) return null;
          const fixed = fixUrl(rawPath);
          const url = Global.file.useUrl + 'files/' + fixed;
          const base64 = await ConvertImageToBase64(url);
          if (!base64 || base64 === 'data:application/octet-stream;base64,') {
            return null;
          }
          if (base64.startsWith('data:image/')) {
            return base64.replace(/^data:image\/[a-zA-Z0-9.+-]+;base64,/, '');
          }
          return null;
        };
        eduImg1 = await loadBase64(eduImagePath1);
        eduImg2 = await loadBase64(eduImagePath2);
      } catch (error) {
        console.error('[PrintLogic1 image convert error]', error);
        eduImg1 = null;
        eduImg2 = null;
      }
    }

    const isPrintEtcEdu = (
      baseRow.eduTypeNm ||
      baseRow.EDU_TYPE_NM ||
      ''
    ).includes('기타교육');

    const printEduNm = isPrintEtcEdu
      ? convertHtmlBrToNewLine(
        baseRow.eduNmCd ||
        baseRow.EDU_NM_CD ||
        baseRow.etcEduNm ||
        baseRow.ETC_EDU_NM ||
        baseRow.eduNmNm ||
        baseRow.EDU_NM_NM ||
        ''
      )
      : convertHtmlBrToNewLine(
        baseRow.eduNmNm || baseRow.EDU_NM_NM || ''
      );

    const printEduContents = isPrintEtcEdu
      ? convertHtmlBrToNewLine(
        baseRow.eduContentsCd ||
        baseRow.EDU_CONTENTS_CD ||
        baseRow.etcEduContents ||
        baseRow.ETC_EDU_CONTENTS ||
        baseRow.eduContentsNm ||
        baseRow.EDU_CONTENTS_NM ||
        ''
      )
      : convertHtmlBrToNewLine(
        baseRow.eduContentsNm || baseRow.EDU_CONTENTS_NM || ''
      );

    const eduDtVal =
      baseRow.eduDt || baseRow.EDU_DT || '';
    const headerData = {
      TPM_ID: baseTpmId || '',
      CUST_CD:
        searchRef?.current?.custCd || searchRef?.current?._CUST_CD || '',
      EDU_DT: eduDtVal ? dayjs(eduDtVal).format('YYYY-MM-DD') : '',
      EDU_TM: baseRow.eduTm || baseRow.EDU_TM || '',
      EDU_PLACE: convertHtmlBrToNewLine(
        baseRow.eduPlace || baseRow.EDU_PLACE || ''
      ),
      EDU_MANAGER: convertHtmlBrToNewLine(
        baseRow.eduManager || baseRow.EDU_MANAGER || ''
      ),
      EDU_TYPE_CD: baseRow.eduTypeCd || baseRow.EDU_TYPE_CD || '',
      EDU_NM_CD: baseRow.eduNmCd || baseRow.EDU_NM_CD || '',
      EDU_NM_NM: printEduNm,
      EDU_CONTENTS_CD: baseRow.eduContentsCd || baseRow.EDU_CONTENTS_CD || '',
      EDU_CONTENTS_NM: printEduContents,
      EDU_TARGET_M_CNT: baseRow.eduTargetMCnt || baseRow.EDU_TARGET_M_CNT || '',
      EDU_TARGET_W_CNT: baseRow.eduTargetWCnt || baseRow.EDU_TARGET_W_CNT || '',
      EDU_TARGET_CNT: baseRow.eduTargetCnt || baseRow.EDU_TARGET_CNT || '',
      EDU_EXEC_M_CNT: baseRow.eduExecMCnt || baseRow.EDU_EXEC_M_CNT || '',
      EDU_EXEC_W_CNT: baseRow.eduExecWCnt || baseRow.EDU_EXEC_W_CNT || '',
      EDU_EXEC_CNT: baseRow.eduExecCnt || baseRow.EDU_EXEC_CNT || '',
      EDU_NO_EXEC_M_CNT:
        baseRow.eduNoExecMCnt || baseRow.EDU_NO_EXEC_M_CNT || '',
      EDU_NO_EXEC_W_CNT:
        baseRow.eduNoExecWCnt || baseRow.EDU_NO_EXEC_W_CNT || '',
      EDU_NO_EXEC_CNT: baseRow.eduNoExecCnt || baseRow.EDU_NO_EXEC_CNT || '',
      EDU_NO_EXEC_REASON1: convertHtmlBrToNewLine(
        baseRow.eduNoExecReason1 || baseRow.EDU_NO_EXEC_REASON1 || ''
      ),
      EDU_NO_EXEC_REASON2: convertHtmlBrToNewLine(
        baseRow.eduNoExecReason2 || baseRow.EDU_NO_EXEC_REASON2 || ''
      ),
      EDU_NO_EXEC_REASON3: convertHtmlBrToNewLine(
        baseRow.eduNoExecReason3 || baseRow.EDU_NO_EXEC_REASON3 || ''
      ),
      EDU_IMAGE_FILE_PATH1: eduImg1 ?? null,
      EDU_IMAGE_FILE_PATH2: eduImg2 ?? null,
      SIGNIFICANT: convertHtmlBrToNewLine(
        baseRow.significant || baseRow.SIGNIFICANT || ''
      ),
      CREATE_DATE: baseRow.createDate || baseRow.CREATE_DATE || '',
      CREATE_NM: baseRow.createNm || baseRow.CREATE_NM || '',
    };

    const tableData3 = [
      {
        EDU_DT: eduDtVal ? dayjs(eduDtVal).format('YYYY-MM-DD') : '',
        EDU_TM: baseRow.eduTm || baseRow.EDU_TM || '',
        EDU_PLACE: convertHtmlBrToNewLine(
          baseRow.eduPlace || baseRow.EDU_PLACE || ''
        ),
        EDU_MANAGER: convertHtmlBrToNewLine(
          baseRow.eduManager || baseRow.EDU_MANAGER || ''
        ),
        EDU_NM_NM: printEduNm,
        EDU_TYPE_NM: convertHtmlBrToNewLine(
          baseRow.eduTypeNm || baseRow.EDU_TYPE_NM || ''
        ),
      },
    ];

    // 교육대상자 조회 (관리자 Search2Async와 동일 - getTraCustom02R2)
    let memberList = [];
    try {
      const memberRes = await apiRequest(
        'post',
        Global.urls.DocsManagement.getTraCustom02R2,
        { TpmId: Commons.EncryptAES(baseTpmId) || '' }
      );
      const data = Array.isArray(memberRes) ? memberRes : memberRes?.data;
      memberList = Array.isArray(data) ? data : data ? [data] : [];
      if (memberList.length > 0 && memberList[0]?.type === 'Error') {
        memberList = [];
      }
    } catch (e) {
      console.error('[PrintLogic1 교육대상자 조회]', e);
      memberList = [];
    }

    const safeMembers = memberList;
    const total = safeMembers.length;

    let leftCount = 20;
    let rightCount = 20;
    if (total > 40) {
      leftCount = Math.ceil(total / 2);
      rightCount = leftCount;
    }

    const leftData = safeMembers.slice(0, leftCount);
    const rightData = safeMembers.slice(leftCount, leftCount + rightCount);

    while (rightData.length < rightCount) {
      rightData.push({
        userNm: '',
        jobType: '',
        remark: '',
      });
    }

    if (total <= 40) {
      while (leftData.length < 20) {
        leftData.push({ userNm: '', jobType: '', remark: '' });
      }
      while (rightData.length < 20) {
        rightData.push({ userNm: '', jobType: '', remark: '' });
      }
    }

    const tableData1 = [];
    const loopCount = total > 40 ? leftCount : 20;
    for (let i = 0; i < loopCount; i++) {
      const left = leftData[i] || {};
      const right = rightData[i] || {};
      tableData1.push({
        NO_L: total > 40 ? i + 1 : i + 1,
        USER_NM_L: left.userNm || left.USER_NM || '',
        JOB_TYPE_L: left.jobType || left.JOB_TYPE || '',
        REMARK_L: left.remark || left.REMARK || '',
        NO_R: total > 40 ? leftCount + i + 1 : 20 + i + 1,
        USER_NM_R: right.userNm || right.USER_NM || '',
        JOB_TYPE_R: right.jobType || right.JOB_TYPE || '',
        REMARK_R: right.remark || right.REMARK || '',
      });
    }

    const codes = await SearchCode(dispatch, setLoading);
    const eduTypeList =
      (Array.isArray(codes?.EDU_TYPE) && codes.EDU_TYPE.length > 0
        ? codes.EDU_TYPE
        : Array.isArray(codes?.eduType) && codes.eduType?.length > 0
          ? codes.eduType
          : []) || [];

    const tableData2 = [];
    for (let i = 0; i < eduTypeList.length; i += 2) {
      const left = eduTypeList[i];
      const right = eduTypeList[i + 1];
      tableData2.push({
        NO_L: i + 1,
        EDU_TYPE_NM_L: convertHtmlBrToNewLine(left?.name || ''),
        NO_R: right ? i + 2 : '',
        EDU_TYPE_NM_R: convertHtmlBrToNewLine(right?.name || ''),
      });
    }

    const printData = {
      FileName: 'TRA_CUSTOM_02',
      HeaderData: JSON.stringify(headerData),
      TableData1: JSON.stringify(tableData1),
      TableData2: JSON.stringify(tableData2),
      TableData3: JSON.stringify(tableData3),
    };

    const response = await Commons.Axios(
      'report',
      Global.base.useUrl + Global.urls.Reports.reports,
      printData
    );

    if (response.data instanceof Blob) {
      return response.data;
    }
    if (response.data) {
      return new Blob([response.data], { type: 'application/pdf' });
    }
    console.error('PrintLogic1: 응답 데이터가 없습니다.');
    return null;
  } catch (e) {
    console.error(e);
    dispatch(
      showSnackbar({
        message: e.message || '인쇄 데이터 생성에 실패했습니다.',
        type: 'error',
      })
    );
    return null;
  } finally {
    setLoading(false);
  }
};
